import React, { useEffect, useState } from 'react';
import Button from '@atlaskit/button';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import UserPicker, { type OptionData } from '@atlaskit/user-picker';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import type {
	RequestTypeSupportedPrincipal,
	RequestTypeSupportedPrincipalType,
} from '../../../../../services/load-request-type-restriction/types.tsx';
import { useSearchPrincipals } from '../../../../../services/search-principals/index.tsx';
import {
	CUSTOMER_ORGANIZATION_ARI_PREFIX,
	GROUP_ARI_PREFIX,
} from '../../../../../services/search-principals/types.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const PrincipalPicker = ({ alreadySelectedPrincipals, addPrincipals }: Props) => {
	const { formatMessage } = useIntl();
	const [query, setQuery] = useState('');
	const [selectedOptions, setSelectedOptions] = useState<OptionData[]>([]);
	const { data, loading, error, fetch } = useSearchPrincipals();
	const options = (data || []).filter(
		(searchedPrincipal) =>
			!alreadySelectedPrincipals.some(
				(selectedPrincipal) =>
					convertOptionToPrincipal(searchedPrincipal).id === selectedPrincipal.id,
			),
	);

	useEffect(() => {
		if (query) {
			fetch(query);
		}
	}, [fetch, query]);

	return (
		<Inline space="space.100">
			<Box xcss={pickerStyles}>
				<UserPicker
					isMulti
					appearance="compact"
					width="100%"
					ariaLabel={formatMessage(messages.ariaLabel)}
					fieldId="request-type-security-principal-picker"
					options={options}
					onChange={(newSelectedPrincipals) => {
						if (Array.isArray(newSelectedPrincipals)) {
							setSelectedOptions(newSelectedPrincipals);
						}
					}}
					value={selectedOptions}
					placeholder={formatMessage(messages.placeholderV2)}
					onInputChange={(queryStr) => setQuery(queryStr || '')}
					isLoading={loading}
					addMoreMessage=""
					noOptionsMessage={error ? formatMessage(messages.errorV2) : null}
					styles={{
						menuPortal: (base) => ({
							...base,
							zIndex: layers.modal + 1,
						}),
					}}
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					menuPortalTarget={document.body}
				/>
			</Box>
			{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
			<Box
				xcss={addButtonStyles}
				onClick={() => {
					addPrincipals(selectedOptions.map(convertOptionToPrincipal));
					setSelectedOptions([]);
				}}
			>
				<Button shouldFitContainer isDisabled={selectedOptions.length === 0}>
					<FormattedMessage {...messages.add} />
				</Button>
			</Box>
		</Inline>
	);
};

const convertOptionToPrincipal = ({
	id,
	name,
	avatarUrl,
}: OptionData): RequestTypeSupportedPrincipal => {
	let requestTypeSupportedPrincipalType: RequestTypeSupportedPrincipalType = 'USER';
	if (id.startsWith(CUSTOMER_ORGANIZATION_ARI_PREFIX)) {
		requestTypeSupportedPrincipalType = 'CUSTOMER_ORGANIZATION';
	} else if (id.startsWith(GROUP_ARI_PREFIX)) {
		requestTypeSupportedPrincipalType = 'USER_GROUP';
	}

	return {
		id,
		type: requestTypeSupportedPrincipalType,
		name,
		avatar: avatarUrl,
	};
};

const pickerStyles = xcss({
	width: '90%',
});

const addButtonStyles = xcss({
	width: '10%',
});
